import React, { useState, useEffect } from 'react'
import { Redirect } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons/faCircleNotch'
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle'

import { toast } from 'react-toastify'

import Modal from '../../Modal'

import { CREATE, DELETE, useClient } from '@peracto/client'

const contentURLs = ['pages', 'articles', 'templates', 'elements']

const EditRows = ({ selectedRows, instance, reloadData, allowDuplicate = false, history }) => {
    const [errors, setErrors] = useState([])
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showDuplicateModal, setShowDuplicateModal] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isDuplicating, setIsDuplicating] = useState(false)
    const [duplicateRedirectUrl, setDuplicateRedirectUrl] = useState(null)
    const { client } = useClient()

    useEffect(() => {
        if (showDuplicateModal || showDeleteModal) {
            setErrors([])
        }
    }, [showDuplicateModal, showDeleteModal])

    const onDuplicate = async () => {
        setIsDuplicating(true)
        let url = window.location.pathname.replace(/\//g, '')
        let updatedContents = []

        if (contentURLs.includes(url)) {
            url = 'contents'
        }

        if (url === 'variants') {
            url = 'product-variants'
        }

        selectedRows.forEach(row => {
            const content = instance?.data?.find(content => content?.originId === parseInt(row))
            const contentProductLinks = []

            // Used for managing content product links as API only accepts the data in this format
            content?.contentProductLinks?.forEach(contentProductLink => {
                contentProductLinks?.push({
                    product: contentProductLink?.product['@id'],
                    productDisplayOrder: contentProductLink?.productDisplayOrder,
                })
            })

            // Rename name and slug to identify as copy
            const updatedContent = {
                ...content,
                name: `Copy of ${content?.name}`,
                slug: `copy-of-${content?.slug}`,
                contentProductLinks: contentProductLinks,
            }

            // Delete unique data
            delete updatedContent['@id']
            delete updatedContent['@type']
            delete updatedContent?.id
            delete updatedContent?.originId
            delete updatedContent?.createdAt
            delete updatedContent?.publishedAt
            delete updatedContent?.modifiedAt
            delete updatedContent?.modifiedBy

            updatedContents.push(
                client(CREATE, url, {
                    data: { ...updatedContent },
                })
            )
        })

        try {
            const data = await Promise.all(updatedContents)
            setShowDuplicateModal(false)
            setIsDuplicating(false)
            instance.toggleRowSelectedAll(false)
            reloadData()

            const { originId, type } = data?.length > 0 && data[0]?.data

            setDuplicateRedirectUrl(`content/edit/${type}/${originId.toString()}`)
        } catch (e) {
            if (process.env.NODE_ENV === 'development') console.error(e)
            setErrors([
                ...errors,
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...',
            ])
            setIsDuplicating(false)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
        }
    }

    const onDelete = async () => {
        setIsDeleting(true)
        let url = window.location.pathname.replace(/\//g, '')
        let ids = []

        if (contentURLs.includes(url)) {
            url = 'contents'
        }

        if (url === 'variants') {
            url = 'product-variants'
        }

        selectedRows.forEach(row => {
            ids.push(
                client(DELETE, url, {
                    id: `${url}/${row}`,
                })
            )
        })

        try {
            await Promise.all(ids)
            setShowDeleteModal(false)
            setIsDeleting(false)
            instance.toggleRowSelectedAll(false)
            reloadData()
        } catch (e) {
            if (process.env.NODE_ENV === 'development') console.error(e)
            setErrors([
                ...errors,
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...',
            ])
            setIsDeleting(false)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
        }
    }

    const hasErrors = errors.length > 0

    let primaryButtonText = ''
    if (isDeleting) primaryButtonText = 'Deleting...'
    if (isDuplicating) primaryButtonText = 'Duplicating...'
    if (hasErrors && !isDeleting && !isDuplicating) {
        primaryButtonText = 'Cancel'
    } else {
        if (showDeleteModal) primaryButtonText = `Delete Row${selectedRows.length !== 1 ? `s` : ''}`
        if (showDuplicateModal)
            primaryButtonText = `Duplicate Row${selectedRows.length !== 1 ? `s` : ''}`
    }

    let modalContent
    if (hasErrors && !isDeleting && !isDuplicating) modalContent = errors.join('\n\n')
    else {
        if (showDeleteModal) {
            modalContent = `Are you sure you would like to permanently delete the ${
                selectedRows.length
            } selected row${selectedRows.length !== 1 ? `s` : ''}?`
        }

        if (showDuplicateModal) {
            modalContent = `Are you sure you would like to duplicate the selected row?`
        }
    }

    if (duplicateRedirectUrl) {
        return <Redirect to={duplicateRedirectUrl} />
    }

    return (
        <>
            <div className="!flex !items-center">
                <FontAwesomeIcon icon={faTrash} size="1x" className="!mr-2" />
                <p>
                    Edit {selectedRows.length} row{selectedRows.length != 1 && `s`}.
                </p>
            </div>

            <div>
                <button
                    className="btn btn-outline-secondary !mr-2 btn-cancel-delete"
                    onClick={() => instance.toggleRowSelectedAll(false)}
                    data-testid="cancel-delete-button"
                >
                    Cancel
                </button>
                {allowDuplicate && selectedRows?.length < 2 && (
                    <button
                        className="btn btn-outline-primary btn-open-modal !mr-2"
                        onClick={() => setShowDuplicateModal(true)}
                        data-testid="duplicate-button"
                    >
                        Duplicate
                    </button>
                )}
                <button
                    className="btn btn-danger btn-open-modal"
                    onClick={() => setShowDeleteModal(true)}
                    data-testid="delete-button"
                >
                    Delete
                </button>

                <Modal
                    isVisible={showDuplicateModal}
                    title="Duplicate Row"
                    close={() => setShowDuplicateModal(false)}
                    buttons={[
                        {
                            type: 'btn-outline-secondary',
                            text: 'Close',
                            action: () => setShowDuplicateModal(false),
                        },
                        {
                            type: 'btn-primary',
                            text: primaryButtonText,
                            disabled: isDuplicating,
                            action: () => {
                                if (hasErrors) {
                                    setShowDuplicateModal(false)
                                    instance.toggleRowSelectedAll(false)
                                } else {
                                    onDuplicate()
                                }
                            },
                        },
                    ]}
                >
                    {isDuplicating ? (
                        <>
                            <FontAwesomeIcon
                                icon={faCircleNotch}
                                size="4x"
                                spin
                                className="!block !mb-6"
                            />
                            Duplicating.....
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                size="4x"
                                className="!block !mb-6"
                            />
                            <div data-testid={`duplicate-warning`}>{modalContent}</div>
                        </>
                    )}
                </Modal>

                <Modal
                    isVisible={showDeleteModal}
                    title="Delete Rows"
                    close={() => setShowDeleteModal(false)}
                    buttons={[
                        {
                            type: 'btn-outline-secondary',
                            text: 'Close',
                            action: () => setShowDeleteModal(false),
                        },
                        {
                            type: hasErrors ? 'btn-outline-secondary' : 'btn-danger',
                            text: primaryButtonText,
                            disabled: isDeleting,
                            action: () => {
                                if (hasErrors) {
                                    setShowDeleteModal(false)
                                    instance.toggleRowSelectedAll(false)
                                } else {
                                    onDelete()
                                }
                            },
                        },
                    ]}
                >
                    {isDeleting ? (
                        <>
                            <FontAwesomeIcon
                                icon={faCircleNotch}
                                size="4x"
                                spin
                                className="!block !mb-6"
                            />
                            Deleting.....
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                size="4x"
                                className="!block !mb-6"
                            />
                            <div data-testid={`delete-warning`}>{modalContent}</div>
                        </>
                    )}
                </Modal>
            </div>
        </>
    )
}

export default EditRows
